<script setup>
/* global axios */
// Formulário de criar/editar um servidor MCP INTERNO. Sem URL/transporte/auth
// (geramos tudo): nome + descrição + quais CAPACIDADES o servidor expõe
// (checkboxes vindos do catálogo) + ativado.
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  server: {
    type: Object,
    default: null,
  },
});
const emit = defineEmits(['saved', 'close']);

const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/mcp/servers`;

const isEdit = computed(() => Boolean(props.server?.id));
const saving = ref(false);
const catalog = ref([]);

const form = reactive({
  name: props.server?.name || '',
  description: props.server?.description || '',
  capabilities: [...(props.server?.capabilities || [])],
  enabled: props.server ? props.server.enabled : true,
});

const isValid = computed(
  () => form.name.trim() && form.capabilities.length > 0
);

const toggleCapability = key => {
  const i = form.capabilities.indexOf(key);
  if (i === -1) form.capabilities.push(key);
  else form.capabilities.splice(i, 1);
};

const fetchCatalog = async () => {
  try {
    const { data } = await axios.get(`${base}/capabilities`);
    catalog.value = data.data || [];
  } catch (e) {
    catalog.value = [];
  }
};

const submit = async () => {
  if (!isValid.value) return;
  saving.value = true;
  const payload = {
    name: form.name.trim(),
    description: form.description,
    capabilities: form.capabilities,
    enabled: form.enabled,
  };
  try {
    let saved;
    if (isEdit.value) {
      const { data } = await axios.patch(`${base}/${props.server.id}`, {
        mcp_server: payload,
      });
      saved = data.data;
      useAlert(t('MCP_MGMT.EDIT.SUCCESS'));
    } else {
      const { data } = await axios.post(base, { mcp_server: payload });
      saved = data.data;
      useAlert(t('MCP_MGMT.ADD.SUCCESS'));
    }
    emit('saved', { server: saved, isNew: !isEdit.value });
  } catch (e) {
    const msg =
      e.response?.data?.errors?.join(', ') ||
      t(isEdit.value ? 'MCP_MGMT.EDIT.ERROR' : 'MCP_MGMT.ADD.ERROR');
    useAlert(msg);
  } finally {
    saving.value = false;
  }
};

onMounted(fetchCatalog);
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto w-[32rem] max-w-full">
    <woot-modal-header
      :header-title="
        isEdit ? t('MCP_MGMT.FORM.EDIT_TITLE') : t('MCP_MGMT.FORM.CREATE_TITLE')
      "
      :header-content="
        isEdit ? t('MCP_MGMT.FORM.EDIT_DESC') : t('MCP_MGMT.FORM.CREATE_DESC')
      "
    />
    <form class="flex flex-col gap-1 px-8 py-2" @submit.prevent="submit">
      <woot-input
        v-model="form.name"
        class="w-full"
        :label="t('MCP_MGMT.FORM.NAME.LABEL')"
        :placeholder="t('MCP_MGMT.FORM.NAME.PLACEHOLDER')"
      />

      <woot-input
        v-model="form.description"
        class="w-full"
        :label="t('MCP_MGMT.FORM.DESCRIPTION.LABEL')"
        :placeholder="t('MCP_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
      />

      <!-- o que este MCP oferece: capacidades por módulo -->
      <div class="w-full mt-2">
        <span class="block mb-1 text-sm font-medium text-n-slate-12">
          {{ t('MCP_MGMT.FORM.CAPABILITIES.LABEL') }}
        </span>
        <p class="mb-2 text-xs text-n-slate-10">
          {{ t('MCP_MGMT.FORM.CAPABILITIES.HELP') }}
        </p>
        <div class="flex flex-col gap-2">
          <label
            v-for="cap in catalog"
            :key="cap.key"
            class="flex items-start w-full gap-2.5 p-3 border rounded-lg cursor-pointer transition-colors"
            :class="
              form.capabilities.includes(cap.key)
                ? 'border-n-brand bg-n-brand/5'
                : 'border-n-weak hover:border-n-slate-6'
            "
          >
            <input
              type="checkbox"
              class="mt-0.5 shrink-0 accent-n-brand"
              :checked="form.capabilities.includes(cap.key)"
              @change="toggleCapability(cap.key)"
            />
            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-sm font-medium text-n-slate-12">
                {{ cap.label }}
                <span class="text-xs font-normal text-n-slate-10">
                  · {{ cap.tools.length }} {{ t('MCP_MGMT.TOOLS_WORD') }}
                </span>
              </span>
              <span class="text-xs text-n-slate-11">{{ cap.description }}</span>
            </div>
          </label>
        </div>
      </div>

      <div class="flex items-center w-full gap-2 py-3">
        <input
          id="mcp-enabled"
          v-model="form.enabled"
          type="checkbox"
          class="accent-n-brand"
          :value="true"
        />
        <label for="mcp-enabled" class="text-sm text-n-slate-12">
          {{ t('MCP_MGMT.FORM.ENABLED.LABEL') }}
        </label>
      </div>

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="t('MCP_MGMT.FORM.CANCEL')"
          @click.prevent="emit('close')"
        />
        <NextButton
          type="submit"
          :label="isEdit ? t('MCP_MGMT.FORM.SAVE') : t('MCP_MGMT.FORM.CREATE')"
          :disabled="!isValid || saving"
          :is-loading="saving"
        />
      </div>
    </form>
  </div>
</template>
